import type { BoardFilter } from "~/features/board/types";
import type { GalleryKind, GalleryStatus } from "~/features/gallery/types";
import type { ChatTab } from "~/features/messages/types";
import type { SearchFilters } from "~/features/search/types";

export const queryKeys = {
  // A/B 実験の群（算出結果のキャッシュ用）
  experiments: {
    all: ["experiments"] as const,
    cocoplusRecommend: (userId: string) =>
      [...queryKeys.experiments.all, "cocoplusRecommend", userId] as const,
  },
  call: {
    all: ["call"] as const,
    incoming: () => [...queryKeys.call.all, "incoming"] as const,
    consumePoint: (applicationId: string) =>
      [...queryKeys.call.all, "consumePoint", applicationId] as const,
    lovenseMenu: (partnerId: string) =>
      [...queryKeys.call.all, "lovenseMenu", partnerId] as const,
    uncompletedLog: (partnerId: string) =>
      [...queryKeys.call.all, "uncompletedLog", partnerId] as const,
    fallbackUsers: (partnerId: string, limit: number) =>
      [...queryKeys.call.all, "fallbackUsers", partnerId, limit] as const,
  },
  auth: {
    all: ["auth"] as const,
    passwordResetAuthCode: (authCode: string) =>
      [...queryKeys.auth.all, "passwordResetAuthCode", authCode] as const,
    lovenseLiveRecording: () =>
      [...queryKeys.auth.all, "lovenseLiveRecording"] as const,
  },
  user: {
    all: ["user"] as const,
    me: () => [...queryKeys.user.all, "me"] as const,
    profile: (userId: string) =>
      [...queryKeys.user.all, "profile", userId] as const,
    publicImages: (userId: string) =>
      [...queryKeys.user.all, "publicImages", userId] as const,
    point: () => [...queryKeys.user.all, "point"] as const,
    hasPurchased: () => [...queryKeys.user.all, "hasPurchased"] as const,
    consumedPointTotal: () =>
      [...queryKeys.user.all, "consumedPointTotal"] as const,
    giftPurchaseStatus: () =>
      [...queryKeys.user.all, "giftPurchaseStatus"] as const,
    profileMedia: (userId: string) =>
      [...queryKeys.user.all, "profileMedia", userId] as const,
    similarUsers: (userId: string) =>
      [...queryKeys.user.all, "similarUsers", userId] as const,
  },
  autumnPointBack: {
    all: ["autumnPointBack"] as const,
    preferenceMatches: () =>
      [...queryKeys.autumnPointBack.all, "preferenceMatch"] as const,
    preferenceMatch: (userId: string) =>
      [...queryKeys.autumnPointBack.preferenceMatches(), userId] as const,
    progress: () => [...queryKeys.autumnPointBack.all, "progress"] as const,
  },
  newUserPointBack: {
    all: ["newUserPointBack"] as const,
    progress: () => [...queryKeys.newUserPointBack.all, "progress"] as const,
  },
  discover: {
    all: ["discover"] as const,
    list: () => [...queryKeys.discover.all, "list"] as const,
    // フィルタ付きの一覧（新人・近く等）。フィルタ無しは list() をそのまま使う
    filteredList: (filter: { is_new?: true; region?: number[] }) =>
      [...queryKeys.discover.list(), filter] as const,
    /** Carina のさがす画面の条件つき一覧（検索 API で最大 200 件）。region は「近くに住んでる」の居住地 */
    carinaSearches: () => [...queryKeys.discover.all, "carinaSearch"] as const,
    carinaSearch: (filters: SearchFilters, nearbyRegion: number | null) =>
      [...queryKeys.discover.carinaSearches(), filters, nearbyRegion] as const,
    recentVideoCallUsers: () =>
      [...queryKeys.discover.all, "recentVideoCallUsers"] as const,
    /** coco+ のビデオ通話タブ「前に話した子」（通話履歴） */
    talkedBeforeUsers: () =>
      [...queryKeys.discover.all, "talkedBeforeUsers"] as const,
    highReviewScoreUsers: () =>
      [...queryKeys.discover.all, "highReviewScoreUsers"] as const,
    // 取得件数を指定した評価が高い女の子（coco+ のタブ用。通常キーのプレフィックスを共有する）
    highReviewScoreUsersWithLimit: (limit: number) =>
      [...queryKeys.discover.highReviewScoreUsers(), { limit }] as const,
    // coco+ のおすすめ枠（A/B の B 群のみ取得）
    cocoplusRecommendedUsers: () =>
      [...queryKeys.discover.all, "cocoplusRecommendedUsers"] as const,
    preferenceMatchUsers: () =>
      [...queryKeys.discover.all, "preferenceMatchUsers"] as const,
    // 秋のポイントバック開催中の「好みに合う子」（3分以上通話した女性を除いた版）。
    // 通常版のキーを接頭辞にするので、既存の invalidate（preferenceMatchUsers()）にも含まれる
    preferenceMatchUsersExcludingLongCall: () =>
      [
        ...queryKeys.discover.preferenceMatchUsers(),
        "excludingLongCall",
      ] as const,
    newcomers: () => [...queryKeys.discover.all, "newcomers"] as const,
    // S/M な性格の女の子（meet_people_version_2 を personalities で絞った一覧）
    personalityUsers: (kind: "sadistic" | "masochistic") =>
      [...queryKeys.discover.all, "personalityUsers", kind] as const,
    // おすすめ画面の「最近ログインした順」（さがす一覧 meet_people の先頭）
    recentLoginUsers: () =>
      [...queryKeys.discover.all, "recentLoginUsers"] as const,
    recommendRanking: () =>
      [...queryKeys.discover.all, "recommendRanking"] as const,
    // 「もっと見る」一覧（ページ分割）。既存セクションのキーとはプレフィックスを共有しない
    seeMore: (section: string) =>
      [...queryKeys.discover.all, "seeMore", section] as const,
    // 3分以上通話した女性を除いた版の「もっと見る」。通常版のキーを接頭辞にする
    seeMoreExcludingLongCall: (section: string) =>
      [...queryKeys.discover.seeMore(section), "excludingLongCall"] as const,
  },
  // おかえりプラン照会。さがす画面（ポップアップ）・購入画面（最上部）の両方から参照するため
  // discover 配下から独立させている
  comebackPurchasePlan: {
    all: ["comebackPurchasePlan"] as const,
    plan: (markShown: boolean) =>
      [...queryKeys.comebackPurchasePlan.all, "plan", markShown] as const,
  },
  search: {
    all: ["search"] as const,
    lists: () => [...queryKeys.search.all, "list"] as const,
    list: (filters: SearchFilters | null) =>
      [...queryKeys.search.lists(), filters] as const,
  },
  chat: {
    all: ["chat"] as const,
    conversations: (tab?: ChatTab) =>
      tab !== undefined
        ? ([...queryKeys.chat.all, "conversations", tab] as const)
        : ([...queryKeys.chat.all, "conversations"] as const),
    /** 最後のメッセージが自分の会話（list_conversation の answered_only） */
    answeredConversations: () =>
      [...queryKeys.chat.conversations(), "answered"] as const,
    /** 相手との履歴に自分・相手それぞれの送信があるか（最後のメッセージ時刻ごと） */
    messageSides: (frdId: string, lastMessageAt: string | null) =>
      [...queryKeys.chat.all, "messageSides", frdId, lastMessageAt] as const,
    messages: (frdId: string) =>
      [...queryKeys.chat.all, "messages", frdId] as const,
    openedImages: () => [...queryKeys.chat.all, "openedImages"] as const,
    openedVideos: () => [...queryKeys.chat.all, "openedVideos"] as const,
    openedAudios: () => [...queryKeys.chat.all, "openedAudios"] as const,
    totalUnread: () => [...queryKeys.chat.all, "totalUnread"] as const,
  },
  userPreferences: {
    all: ["userPreferences"] as const,
    detail: () => [...queryKeys.userPreferences.all, "detail"] as const,
  },
  myProfile: {
    all: ["myProfile"] as const,
    detail: () => [...queryKeys.myProfile.all, "detail"] as const,
    avatarReview: (imageId: string) =>
      [...queryKeys.myProfile.all, "avatarReview", imageId] as const,
  },
  moderation: {
    all: ["moderation"] as const,
    blockList: () => [...queryKeys.moderation.all, "blockList"] as const,
  },
  notifications: {
    all: ["notifications"] as const,
    settings: () => [...queryKeys.notifications.all, "settings"] as const,
  },
  board: {
    all: ["board"] as const,
    lists: () => [...queryKeys.board.all, "list"] as const,
    list: (filter: BoardFilter) =>
      [...queryKeys.board.lists(), filter] as const,
  },
  dailyBonus: {
    all: ["dailyBonus"] as const,
    /** 受取（utage_web_daily_bonus）＋残高＋購入コース可否を 1 フェッチで集約 */
    data: () => [...queryKeys.dailyBonus.all, "data"] as const,
  },
  fukadaCampaign: {
    all: ["fukadaCampaign"] as const,
    schedule: () => [...queryKeys.fukadaCampaign.all, "schedule"] as const,
    broadcasterLive: () =>
      [...queryKeys.fukadaCampaign.all, "broadcasterLive"] as const,
    pointBackProgress: () =>
      [...queryKeys.fukadaCampaign.all, "pointBackProgress"] as const,
    lotteryInfo: () =>
      [...queryKeys.fukadaCampaign.all, "lotteryInfo"] as const,
    consumePointRanking: () =>
      [...queryKeys.fukadaCampaign.all, "consumePointRanking"] as const,
  },
  review: {
    all: ["review"] as const,
    list: (userId: string) =>
      [...queryKeys.review.all, "list", userId] as const,
    postingEnabled: (targetUserId: string) =>
      [...queryKeys.review.all, "postingEnabled", targetUserId] as const,
    badgeGrantStatus: (targetUserId: string) =>
      [...queryKeys.review.all, "badgeGrantStatus", targetUserId] as const,
    badgeProgress: (targetUserId: string) =>
      [...queryKeys.review.all, "badgeProgress", targetUserId] as const,
  },
  lovense: {
    all: ["lovense"] as const,
    menu: (callType: "live" | "video", partnerId: string) =>
      [...queryKeys.lovense.all, "menu", callType, partnerId] as const,
  },
  present: {
    all: ["present"] as const,
    menu: (callType: "live" | "video", partnerId: string) =>
      [...queryKeys.present.all, "menu", callType, partnerId] as const,
  },
  live: {
    all: ["live"] as const,
    channels: () => [...queryKeys.live.all, "channels"] as const,
    lovenseMenu: (broadcasterId: string) =>
      [...queryKeys.live.all, "lovenseMenu", broadcasterId] as const,
    uncompletedLog: (broadcasterId: string) =>
      [...queryKeys.live.all, "uncompletedLog", broadcasterId] as const,
    presentMenuSummary: (channelId: string) =>
      [...queryKeys.live.all, "presentMenuSummary", channelId] as const,
    ngWordList: () => [...queryKeys.live.all, "ngWordList"] as const,
    superChatMenu: (partnerId: string) =>
      [...queryKeys.live.all, "superChatMenu", partnerId] as const,
    livePollMenu: (partnerId: string) =>
      [...queryKeys.live.all, "livePollMenu", partnerId] as const,
    livePoll: (partnerId: string) =>
      [...queryKeys.live.all, "livePoll", partnerId] as const,
    watchHistory: () => [...queryKeys.live.all, "watchHistory"] as const,
  },
  creditPurchaseCourse: {
    all: ["creditPurchaseCourse"] as const,
    info: () => [...queryKeys.creditPurchaseCourse.all, "info"] as const,
  },
  gallery: {
    all: ["gallery"] as const,
    items: (kind: GalleryKind, status: GalleryStatus) =>
      [...queryKeys.gallery.all, "items", kind, status] as const,
  },
  footprint: {
    all: ["footprint"] as const,
    list: () => [...queryKeys.footprint.all, "list"] as const,
    // おすすめ画面の先頭だけの取得（足跡一覧の無限クエリとキャッシュを分ける）
    preview: () => [...queryKeys.footprint.all, "preview"] as const,
  },
  bookmark: {
    all: ["bookmark"] as const,
    list: () => [...queryKeys.bookmark.all, "list"] as const,
    activities: () => [...queryKeys.bookmark.all, "activities"] as const,
  },
  like: {
    all: ["like"] as const,
    received: () => [...queryKeys.like.all, "received"] as const,
    sent: () => [...queryKeys.like.all, "sent"] as const,
  },
  demoCall: {
    all: ["demoCall"] as const,
    info: () => [...queryKeys.demoCall.all, "info"] as const,
  },
  fleamarket: {
    all: ["fleamarket"] as const,
    items: () => [...queryKeys.fleamarket.all, "items"] as const,
    itemsList: (params: Record<string, unknown>) =>
      [...queryKeys.fleamarket.items(), params] as const,
    itemDetail: (itemId: string) =>
      [...queryKeys.fleamarket.all, "itemDetail", itemId] as const,
    favorites: (userId: string) =>
      [...queryKeys.fleamarket.all, "favorites", userId] as const,
    transactions: (userId: string) =>
      [...queryKeys.fleamarket.all, "transactions", userId] as const,
    transactionDetail: (transactionId: string) =>
      [
        ...queryKeys.fleamarket.all,
        "transactionDetail",
        transactionId,
      ] as const,
    shipmentDetail: (userId: string, transactionId: string) =>
      [
        ...queryKeys.fleamarket.all,
        "shipmentDetail",
        userId,
        transactionId,
      ] as const,
    yamatoDeliveryStatus: (userId: string, transactionId: string) =>
      [
        ...queryKeys.fleamarket.all,
        "yamatoDeliveryStatus",
        userId,
        transactionId,
      ] as const,
    address: (userId: string) =>
      [...queryKeys.fleamarket.all, "address", userId] as const,
    seller: (sellerId: string) =>
      [...queryKeys.fleamarket.all, "seller", sellerId] as const,
  },
} as const;
